/**
 * Announcement Banner Component
 * Shows site-wide announcements from admin
 */
import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, Info, CheckCircle, AlertCircle } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AnnouncementBanner = () => {
  const [announcement, setAnnouncement] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const fetchAnnouncement = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/admin/announcement`);
        if (response.ok) {
          const data = await response.json();
          if (data.announcement) {
            // Check if user has dismissed this announcement
            const dismissedId = localStorage.getItem('dismissedAnnouncement');
            if (dismissedId !== data.announcement.message) {
              setAnnouncement(data.announcement);
              setDismissed(false);
              // Dispatch event so other components can adjust
              window.dispatchEvent(new CustomEvent('announcementVisible', { detail: true }));
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch announcement:', error);
      }
    };

    fetchAnnouncement();
    // Refresh every 5 minutes
    const interval = setInterval(fetchAnnouncement, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const handleDismiss = () => {
    if (announcement) {
      localStorage.setItem('dismissedAnnouncement', announcement.message);
    }
    setDismissed(true);
    // Dispatch event so other components can adjust
    window.dispatchEvent(new CustomEvent('announcementVisible', { detail: false }));
  };

  if (!announcement || dismissed) return null;

  const getStyles = () => {
    switch (announcement.type) {
      case 'warning':
        return {
          bg: 'bg-gradient-to-r from-amber-500 to-orange-500',
          icon: <AlertTriangle className="w-4 h-4" />
        };
      case 'error':
        return {
          bg: 'bg-gradient-to-r from-red-500 to-rose-600',
          icon: <AlertCircle className="w-4 h-4" />
        };
      case 'success':
        return {
          bg: 'bg-gradient-to-r from-emerald-500 to-green-600',
          icon: <CheckCircle className="w-4 h-4" />
        };
      default:
        return {
          bg: 'bg-gradient-to-r from-blue-500 to-indigo-600',
          icon: <Info className="w-4 h-4" />
        };
    }
  };

  const styles = getStyles();

  return (
    <div className={`${styles.bg} text-white py-2 px-4`} data-testid="announcement-banner">
      <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-sm">
        {styles.icon}
        <span className="font-medium">{announcement.message}</span>
        <button
          onClick={handleDismiss}
          className="ml-4 p-1 hover:bg-white/20 rounded transition-colors"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default AnnouncementBanner;
